import React, { useState } from "react";
import { TableCellsIcon, EyeIcon, ChartBarIcon, ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { TableData, formatCellValue, isSpaceNumberField, isTimeSeriesField, getUnderOverColorClass, Visualization } from "../../types/table";
import TableModal from "../TableModal/TableModal";
import ChartVisualization from "../ChartVisualization";
import Sparkline from "../Sparkline";

interface TablePreviewProps {
  tableData: TableData;
  title?: string;
  maxPreviewRows?: number;
  onRowClick?: (rowData: Record<string, any>) => void;
  visualizations?: Visualization[];
  mallName?: string;
  currentPlanName?: string;
  currentPropertyNumber?: string;
  onSwitchPlan?: (planName: string, propertyNumber?: string) => void;
  onHighlightAll?: () => void;
  isHighlightActive?: boolean;
}

const TablePreview: React.FC<TablePreviewProps> = ({
  tableData,
  title = "Data Table",
  maxPreviewRows = 7,
  onRowClick,
  visualizations = [],
  mallName,
  currentPlanName,
  currentPropertyNumber,
  onSwitchPlan,
  onHighlightAll,
  isHighlightActive = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"table" | "charts">("table");

  const { labels, data } = tableData;
  const fieldNames = Object.keys(labels);
  const hasVisualizations = visualizations && visualizations.length > 0;

  // Check if table has space_number field for click interactions
  const hasSpaceNumberField = fieldNames.some((field) =>
    isSpaceNumberField(field)
  );
  const spaceNumberFieldName = fieldNames.find((field) =>
    isSpaceNumberField(field)
  );

  // Handle row click
  const handleRowClick = (row: Record<string, any>) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <>
      <div className="flex-none w-full max-w-5xl bg-white border-l-4 border-t border-r border-b border-gray-200 rounded-lg shadow-md p-4" style={{ borderLeftColor: '#0f3557' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className="p-1.5 rounded-full mr-2" style={{ backgroundColor: '#0f3557' }}>
              <TableCellsIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold" style={{ color: '#0f3557' }}>
                Data Table
              </div>
              <h5 className="text-sm font-semibold text-gray-800">
                {title}
              </h5>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {activeTab === "table" ? (
              <>
                {data.length} row{data.length !== 1 ? "s" : ""}
              </>
            ) : (
              <>
                {visualizations.length} chart{visualizations.length !== 1 ? "s" : ""}
              </>
            )}
          </div>
        </div>

        {/* Tabs - only show if visualizations exist */}
        {hasVisualizations && (
          <div className="flex gap-1 mb-3 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("table")}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                activeTab === "table"
                  ? "border-[#0f3557] text-[#0f3557]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <TableCellsIcon className="h-3 w-3" />
              Table
            </button>
            <button
              onClick={() => setActiveTab("charts")}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                activeTab === "charts"
                  ? "border-[#0f3557] text-[#0f3557]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <ChartBarIcon className="h-3 w-3" />
              Charts
            </button>
          </div>
        )}
        <div className="text-[10px] text-gray-500 mb-2">
          {activeTab === "table" && hasSpaceNumberField && (
            <span>Click rows to highlight spaces on plan</span>
          )}
        </div>

        {/* Content based on active tab */}
        {activeTab === "table" ? (
          /* Table Preview */
          <div className="overflow-x-auto overflow-y-auto max-h-[296px] mb-3">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-300 text-xs">
            <thead className="bg-gray-50">
              <tr>
                {fieldNames.map((fieldName) => (
                  <th
                    key={fieldName}
                    className="px-3 py-2 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap"
                  >
                    {labels[fieldName]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, rowIndex) => {
                const isClickable =
                  hasSpaceNumberField &&
                  spaceNumberFieldName &&
                  row[spaceNumberFieldName];
                
                // Check if this row has a different plan_name
                const rowPlanName = row.plan_name;
                const isDifferentPlan = rowPlanName && rowPlanName !== currentPlanName;
                
                // Check if this row has a different property_number
                const rowPropertyNumber = row.property_number;
                const isDifferentProperty = rowPropertyNumber && rowPropertyNumber !== currentPropertyNumber;

                return (
                  <tr
                    key={rowIndex}
                    onClick={() => isClickable && handleRowClick(row)}
                    className={`${
                      isClickable
                        ? "cursor-pointer transition-colors"
                        : ""
                    }`}
                    style={isClickable ? {
                      cursor: 'pointer',
                    } : {}}
                    onMouseEnter={(e) => {
                      if (isClickable) {
                        e.currentTarget.style.backgroundColor = 'rgba(15, 53, 87, 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isClickable) {
                        e.currentTarget.style.backgroundColor = '';
                      }
                    }}
                  >
                    {fieldNames.map((fieldName) => {
                      const value = row[fieldName];
                      const isTimeSeries = isTimeSeriesField(fieldName) && Array.isArray(value);
                      const formattedValue = isTimeSeries ? "" : formatCellValue(fieldName, value);
                      const isSpaceNumber = isSpaceNumberField(fieldName);
                      const isPlanField = fieldName === 'plan_name';
                      const showSwitchButton = isPlanField && onSwitchPlan && (isDifferentPlan || isDifferentProperty);

                      const underOverClass = getUnderOverColorClass(value);

                      return (
                        <td
                          key={fieldName}
                          className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${
                            isSpaceNumber
                              ? "font-semibold"
                              : isPlanField && isDifferentProperty
                              ? "text-purple-600 font-medium"
                              : isPlanField && isDifferentPlan
                              ? "text-orange-600 font-medium"
                              : underOverClass
                              ? underOverClass
                              : "text-gray-700"
                          }`}
                          style={isSpaceNumber ? { color: '#0f3557' } : {}}
                        >
                          <div className="flex items-center gap-1">
                            {isTimeSeries ? (
                              <Sparkline points={value} width={110} height={28} />
                            ) : (
                              <span>{formattedValue === 'N/A' ? '' : formattedValue}</span>
                            )}
                            {/* Switch Plan button inline with plan_name */}
                            {showSwitchButton && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSwitchPlan(rowPlanName, rowPropertyNumber);
                                }}
                                className={`flex items-center gap-1 p-1 rounded-md transition-colors ${
                                  isDifferentProperty
                                    ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-700'
                                    : 'bg-orange-100 text-orange-600 hover:bg-orange-200 hover:text-orange-700'
                                }`}
                                title={
                                  isDifferentProperty
                                    ? `Switch to property: ${rowPropertyNumber} / plan: ${rowPlanName}`
                                    : `Switch to plan: ${rowPlanName}`
                                }
                              >
                                <ArrowsRightLeftIcon className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        ) : (
          /* Charts Preview */
          <div className="mb-3">
            <div className="grid grid-cols-1 gap-4">
              {visualizations.slice(0, 2).map((visualization, index) => (
                <div
                  key={index}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                >
                  <ChartVisualization visualization={visualization} />
                </div>
              ))}
              {visualizations.length > 2 && (
                <div className="text-center text-xs text-gray-500 py-2">
                  +{visualizations.length - 2} more chart{visualizations.length - 2 !== 1 ? "s" : ""}
                </div>
              )}
            </div>
          </div>
        )}

        {/* View Full Data Button */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div/>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
            style={{ backgroundColor: '#0f3557' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0a2338';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#0f3557';
            }}
          >
            <EyeIcon className="h-4 w-4" />
            {activeTab === "table" ? "View Full Table" : "View All Charts"}
            {activeTab === "charts" && visualizations.length > 2 && (
              <span className="ml-1 text-[10px] opacity-90">
                ({visualizations.length - 2} more)
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Table Modal */}
      <TableModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tableData={tableData}
        title={title}
        onRowClick={onRowClick}
        visualizations={visualizations}
        mallName={mallName}
        currentPlanName={currentPlanName}
        currentPropertyNumber={currentPropertyNumber}
        onSwitchPlan={onSwitchPlan}
        onHighlightAll={onHighlightAll}
        isHighlightActive={isHighlightActive}
      />
    </>
  );
};

export default TablePreview;
